import type { z } from 'zod';
import type {
  AdminPack,
  AdminPackInput,
  AdminPackPatch,
  AdminPacksResponse,
  AdminParticipantsResponse,
  AdminPaymentsResponse,
  AdminRefundStarsResponse,
  AdminStatsResponse,
  AdminThought,
  AuthorUpdate,
  ConsentAcceptInput,
  CostSummaryDto,
  CreditsResponse,
  DraftBlocksInput,
  DraftDto,
  DraftLookInput,
  DraftSummary,
  DraftWriteInput,
  EnterResponse,
  EvalCode,
  EvalItemDto,
  EvalRunDetail,
  EvalRunSummary,
  InviteCreateInput,
  InviteDto,
  ModelCheckResult,
  OrderStatus,
  OrderStatusResponse,
  ParticipantCreditsInput,
  PaymentOptionsResponse,
  Pricing,
  ProfileResponse,
  PublicationKind,
  PromptCode,
  PromptDto,
  PromptSummary,
  PromptVar,
  SettingDto,
  SettingUpdateInput,
  StartPurchaseRequest,
  StartPurchaseResponse,
  ThoughtDto,
  ThoughtSummary,
  WaitlistEntryDto,
  WaitlistJoinInput,
  WaitlistJoinStatus,
  draftEditInputSchema,
  evalItemCreateSchema,
  evalPickSchema,
  evalRunCreateSchema,
  modelCheckInputSchema,
  promptCreateSchema,
  voiceIntentInputSchema,
} from '@app/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Every field of `error` except `code` and `message`, e.g. `{ limit: 10 }`. */
    public details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiDeps {
  baseUrl: string;
  getInitData: () => string;
  fetchImpl?: typeof fetch;
}

export interface BroadcastInput {
  message: string;
  includeOpenButton?: boolean;
}

export interface BroadcastResult {
  ok: boolean;
  audience_size: number;
  sent: number;
  blocked: number;
  failed: number;
}

/** One brain step of GET /admin/prompts. */
export interface PromptStep {
  code: PromptCode;
  active: PromptSummary | null;
  versions: PromptSummary[];
}

/** GET /admin/prompts: the steps and the placeholder values from the passport copy. */
export interface PromptSteps {
  steps: PromptStep[];
  vars: Record<PromptVar, string>;
  /** The values for «Текст схемы»: the limits of «Схема». */
  diagramVars: Record<PromptVar, string>;
  listicleVars: Record<PromptVar, string>;
}

export interface EvalEstimate {
  items: number;
  usd: number;
}

export type DraftEditInput = z.infer<typeof draftEditInputSchema>;
export type PromptCreateInput = z.input<typeof promptCreateSchema>;
export type ModelCheckInput = z.infer<typeof modelCheckInputSchema>;
export type EvalItemCreateInput = z.infer<typeof evalItemCreateSchema>;
export type EvalRunCreateInput = z.infer<typeof evalRunCreateSchema>;
export type EvalPickInput = z.infer<typeof evalPickSchema>;
export type VoiceIntent = z.infer<typeof voiceIntentInputSchema>['intent'];

export interface Api {
  getProfile(): Promise<ProfileResponse>;
  acceptConsent(body: ConsentAcceptInput): Promise<ProfileResponse>;
  getCredits(): Promise<CreditsResponse>;
  getPricing(): Promise<Pricing>;
  joinWaitlist(body: WaitlistJoinInput): Promise<{ status: WaitlistJoinStatus }>;
  /** A person without access asks to be let in by the daily quota (spec 9.1). */
  enter(): Promise<EnterResponse>;

  /** The packs on sale and the methods offered for each right now. */
  getPaymentOptions(): Promise<PaymentOptionsResponse>;
  startPurchase(body: StartPurchaseRequest): Promise<StartPurchaseResponse>;
  getOrderStatus(code: string): Promise<OrderStatusResponse>;

  listThoughts(before?: number): Promise<ThoughtSummary[]>;
  getThought(id: number): Promise<ThoughtDto>;
  createTextThought(text: string): Promise<ThoughtDto>;
  createVoiceThought(blob: Blob, durationMs: number): Promise<ThoughtDto>;
  moreIdeas(id: number): Promise<ThoughtDto>;
  retryThought(id: number): Promise<ThoughtDto>;
  /** «Взять» as the chosen kind; without one the API takes the idea's own. */
  takeIdea(id: number, kind?: PublicationKind): Promise<DraftDto>;

  listDrafts(before?: number): Promise<DraftSummary[]>;
  getDraft(id: number): Promise<DraftDto>;
  editDraft(id: number, body: DraftEditInput): Promise<DraftDto>;
  editDraftByVoice(id: number, blob: Blob, baseVersion: number, durationMs: number): Promise<DraftDto>;
  saveBlocks(id: number, body: DraftBlocksInput): Promise<DraftDto>;
  keepFlag(id: number, flagId: string, version: number): Promise<DraftDto>;
  removeFlag(id: number, flagId: string, version: number): Promise<DraftDto>;
  addSuggestion(id: number, suggestionId: string, version: number): Promise<DraftDto>;
  dismissSuggestion(id: number, suggestionId: string, version: number): Promise<DraftDto>;
  undoDraft(id: number, version: number): Promise<DraftDto>;
  approveDraft(id: number, version: number): Promise<DraftDto>;
  retryDraft(id: number): Promise<DraftDto>;
  /** «Формат и текст»: the format, style and carousel kind, no paid call. */
  setDraftLook(id: number, body: DraftLookInput): Promise<DraftDto>;
  /** «Написать текст», or the text written again for another format. */
  writeDraft(id: number, body: DraftWriteInput): Promise<DraftDto>;

  updateAuthor(body: AuthorUpdate): Promise<ProfileResponse>;
  sendIntro(blob: Blob, durationMs: number): Promise<ProfileResponse>;
  skipIntro(): Promise<ProfileResponse>;
  setVoiceIntent(intent: VoiceIntent): Promise<{ botUrl: string | null }>;

  adminGetStats(): Promise<AdminStatsResponse>;
  adminListInvites(): Promise<{ invites: InviteDto[] }>;
  adminCreateInvite(body: InviteCreateInput): Promise<{ invite: InviteDto }>;
  adminRevokeInvite(id: number): Promise<{ ok: true }>;
  adminListWaitlist(): Promise<{ entries: WaitlistEntryDto[] }>;
  adminAdmit(id: number, credits: number): Promise<{ ok: true; notified: boolean }>;
  adminListParticipants(): Promise<AdminParticipantsResponse>;
  adminAddCredits(
    id: number,
    body: ParticipantCreditsInput,
  ): Promise<{ ok: true; notified: boolean; expiresAt: string }>;
  adminListSettings(): Promise<{ settings: SettingDto[] }>;
  adminUpdateSetting(body: SettingUpdateInput): Promise<{ settings: SettingDto[] }>;
  adminBroadcast(body: BroadcastInput): Promise<BroadcastResult>;
  adminListPacks(): Promise<AdminPacksResponse>;
  adminCreatePack(body: AdminPackInput): Promise<AdminPack>;
  adminUpdatePack(id: number, patch: AdminPackPatch): Promise<AdminPack>;
  adminPayments(opts?: { status?: OrderStatus; before?: number }): Promise<AdminPaymentsResponse>;
  /** «Вернуть звёзды»: Telegram gives the stars back, then the unspent credits are taken. */
  adminRefundStars(code: string): Promise<AdminRefundStarsResponse>;
  /** «Снять кредиты»: the money went back in the provider's cabinet. */
  adminRevokeOrder(code: string): Promise<AdminRefundStarsResponse>;

  adminListPrompts(): Promise<PromptSteps>;
  adminGetPrompt(id: number): Promise<PromptDto>;
  adminCreatePrompt(body: PromptCreateInput): Promise<PromptDto>;
  adminActivatePrompt(id: number): Promise<PromptDto>;
  adminCheckModel(body: ModelCheckInput): Promise<ModelCheckResult>;
  adminCosts(days: number): Promise<CostSummaryDto>;
  adminThoughts(): Promise<AdminThought[]>;
  adminEvalItems(): Promise<EvalItemDto[]>;
  adminAddEvalItem(body: EvalItemCreateInput): Promise<EvalItemDto>;
  adminArchiveEvalItem(id: number): Promise<{ ok: true }>;
  adminEvalEstimate(code: EvalCode): Promise<EvalEstimate>;
  adminCreateEvalRun(body: EvalRunCreateInput): Promise<EvalRunSummary>;
  adminEvalRuns(): Promise<EvalRunSummary[]>;
  adminEvalRun(id: number, reveal?: boolean): Promise<EvalRunDetail>;
  adminEvalPick(id: number, body: EvalPickInput): Promise<EvalRunDetail>;
}

const RECORDING_EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
};

/** The file name the API reads the format from, by the MediaRecorder type. */
export function recordingFileName(mime: string): string {
  const type = mime.split(';')[0]!.trim().toLowerCase();
  return `voice.${RECORDING_EXTENSIONS[type] ?? 'bin'}`;
}

function recordingForm(blob: Blob, durationMs: number, fields: Record<string, string> = {}): FormData {
  const form = new FormData();
  form.append('audio', blob, recordingFileName(blob.type));
  form.append('durationMs', String(Math.round(durationMs)));
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  return form;
}

const pageQuery = (before?: number) => `?limit=20${before ? `&before=${before}` : ''}`;

export function createApi(deps: ApiDeps): Api {
  const doFetch = deps.fetchImpl ?? fetch;

  async function send<T>(method: string, path: string, headers: Record<string, string>, body?: BodyInit): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(`${deps.baseUrl}${path}`, {
        method,
        headers: { ...headers, 'X-Init-Data': deps.getInitData() },
        body,
      });
    } catch (err) {
      throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network error');
    }

    if (!res.ok) {
      let code = 'UNKNOWN';
      let message = `HTTP ${res.status}`;
      let details: Record<string, unknown> = {};
      try {
        const data = (await res.json()) as { error?: Record<string, unknown> };
        if (data.error) {
          const { code: c, message: m, ...rest } = data.error;
          if (typeof c === 'string') code = c;
          if (typeof m === 'string') message = m;
          details = rest;
        }
      } catch {
        // The body is not JSON (a proxy error page): keep the status line.
      }
      throw new ApiError(res.status, code, message, details);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    return send<T>(
      method,
      path,
      { 'Content-Type': 'application/json' },
      body !== undefined ? JSON.stringify(body) : undefined,
    );
  }

  /** Multipart POST: no Content-Type, the browser sets the boundary. */
  function upload<T>(path: string, form: FormData): Promise<T> {
    return send<T>('POST', path, {}, form);
  }

  const thought = (p: Promise<{ thought: ThoughtDto }>) => p.then((r) => r.thought);
  const draft = (p: Promise<{ draft: DraftDto }>) => p.then((r) => r.draft);
  const prompt = (p: Promise<{ prompt: PromptDto }>) => p.then((r) => r.prompt);

  return {
    getProfile: () => request('GET', '/profile'),
    acceptConsent: (body) => request('POST', '/profile/consent', body),
    getCredits: () => request('GET', '/credits'),
    getPricing: () => request('GET', '/pricing'),
    joinWaitlist: (body) => request('POST', '/access/waitlist', body),
    enter: () => request('POST', '/access/enter', {}),

    getPaymentOptions: () => request('GET', '/payments/options'),
    startPurchase: (body) => request('POST', '/payments/orders', body),
    getOrderStatus: (code) => request('GET', `/payments/orders/${encodeURIComponent(code)}`),

    listThoughts: (before) =>
      request<{ thoughts: ThoughtSummary[] }>('GET', `/thoughts${pageQuery(before)}`).then((r) => r.thoughts),
    getThought: (id) => thought(request('GET', `/thoughts/${id}`)),
    createTextThought: (text) => thought(request('POST', '/thoughts/text', { text })),
    createVoiceThought: (blob, durationMs) => thought(upload('/thoughts/voice', recordingForm(blob, durationMs))),
    moreIdeas: (id) => thought(request('POST', `/thoughts/${id}/more-ideas`)),
    retryThought: (id) => thought(request('POST', `/thoughts/${id}/retry`)),
    takeIdea: (id, kind) => draft(request('POST', `/ideas/${id}/take`, kind ? { kind } : undefined)),

    listDrafts: (before) =>
      request<{ drafts: DraftSummary[] }>('GET', `/drafts${pageQuery(before)}`).then((r) => r.drafts),
    getDraft: (id) => draft(request('GET', `/drafts/${id}`)),
    editDraft: (id, body) => draft(request('POST', `/drafts/${id}/edit`, body)),
    editDraftByVoice: (id, blob, baseVersion, durationMs) =>
      draft(upload(`/drafts/${id}/edit-voice`, recordingForm(blob, durationMs, { baseVersion: String(baseVersion) }))),
    saveBlocks: (id, body) => draft(request('PUT', `/drafts/${id}/blocks`, body)),
    keepFlag: (id, flagId, version) => draft(request('POST', `/drafts/${id}/flags/${flagId}/keep`, { version })),
    removeFlag: (id, flagId, version) => draft(request('POST', `/drafts/${id}/flags/${flagId}/remove`, { version })),
    addSuggestion: (id, sid, version) => draft(request('POST', `/drafts/${id}/suggestions/${sid}/add`, { version })),
    dismissSuggestion: (id, sid, version) =>
      draft(request('POST', `/drafts/${id}/suggestions/${sid}/dismiss`, { version })),
    undoDraft: (id, version) => draft(request('POST', `/drafts/${id}/undo`, { version })),
    approveDraft: (id, version) => draft(request('POST', `/drafts/${id}/approve`, { version })),
    retryDraft: (id) => draft(request('POST', `/drafts/${id}/retry`)),
    setDraftLook: (id, body) => draft(request('PATCH', `/drafts/${id}/look`, body)),
    writeDraft: (id, body) => draft(request('POST', `/drafts/${id}/write`, body)),

    updateAuthor: (body) => request('PATCH', '/profile/author', body),
    sendIntro: (blob, durationMs) => upload('/profile/intro', recordingForm(blob, durationMs)),
    skipIntro: () => request('POST', '/profile/intro/skip'),
    setVoiceIntent: (intent) => request('POST', '/profile/voice-intent', { intent }),

    adminGetStats: () => request('GET', '/admin/stats'),
    adminListInvites: () => request('GET', '/admin/invites'),
    adminCreateInvite: (body) => request('POST', '/admin/invites', body),
    adminRevokeInvite: (id) => request('POST', `/admin/invites/${id}/revoke`),
    adminListWaitlist: () => request('GET', '/admin/waitlist'),
    adminAdmit: (id, credits) => request('POST', `/admin/waitlist/${id}/admit`, { credits }),
    adminListParticipants: () => request('GET', '/admin/participants'),
    adminAddCredits: (id, body) => request('POST', `/admin/participants/${id}/credits`, body),
    adminListPacks: () => request('GET', '/admin/packs'),
    adminCreatePack: (body) => request('POST', '/admin/packs', body),
    adminUpdatePack: (id, patch) => request('PATCH', `/admin/packs/${id}`, patch),
    adminPayments: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts.status) q.set('status', opts.status);
      if (opts.before) q.set('before', String(opts.before));
      const qs = q.toString();
      return request('GET', `/admin/payments${qs ? `?${qs}` : ''}`);
    },
    adminRefundStars: (code) => request('POST', `/admin/payments/${encodeURIComponent(code)}/refund-stars`),
    adminRevokeOrder: (code) => request('POST', `/admin/payments/${encodeURIComponent(code)}/revoke`),
    adminListSettings: () => request('GET', '/admin/settings'),
    adminUpdateSetting: (body) => request('PATCH', '/admin/settings', body),
    adminBroadcast: (body) => request('POST', '/admin/broadcast', body),

    adminListPrompts: () => request('GET', '/admin/prompts'),
    adminGetPrompt: (id) => prompt(request('GET', `/admin/prompts/${id}`)),
    adminCreatePrompt: (body) => prompt(request('POST', '/admin/prompts', body)),
    adminActivatePrompt: (id) => prompt(request('POST', `/admin/prompts/${id}/activate`)),
    adminCheckModel: (body) =>
      request<{ result: ModelCheckResult }>('POST', '/admin/prompts/check-model', body).then((r) => r.result),
    adminCosts: (days) =>
      request<{ summary: CostSummaryDto }>('GET', `/admin/costs?days=${days}`).then((r) => r.summary),
    adminThoughts: () =>
      request<{ thoughts: AdminThought[] }>('GET', '/admin/thoughts?limit=50').then((r) => r.thoughts),
    adminEvalItems: () => request<{ items: EvalItemDto[] }>('GET', '/admin/eval/items').then((r) => r.items),
    adminAddEvalItem: (body) =>
      request<{ item: EvalItemDto }>('POST', '/admin/eval/items', body).then((r) => r.item),
    adminArchiveEvalItem: (id) => request('DELETE', `/admin/eval/items/${id}`),
    adminEvalEstimate: (code) => request('GET', `/admin/eval/estimate?code=${code}`),
    adminCreateEvalRun: (body) =>
      request<{ run: EvalRunSummary }>('POST', '/admin/eval/runs', body).then((r) => r.run),
    adminEvalRuns: () => request<{ runs: EvalRunSummary[] }>('GET', '/admin/eval/runs').then((r) => r.runs),
    adminEvalRun: (id, reveal) => request('GET', `/admin/eval/runs/${id}${reveal ? '?reveal=1' : ''}`),
    adminEvalPick: (id, body) => request('POST', `/admin/eval/runs/${id}/pick`, body),
  };
}
